import React, { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import EmployeeAPI from "../../../lib/api/Employee";
import "./style.css";

export default function SearchableEmployeeSelect({
  label,
  required,
  value,
  onChange,
  className = "",
  placeholder = "Search for employee...",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch employees when search term changes
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!isOpen) return;

      setLoading(true);
      try {
        const result = await EmployeeAPI.list({
          search: searchTerm,
          limit: 50,
          active: true,
          sort: "code",
        });

        if (result.status === 200 && result.data) {
          setEmployees(result.data);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchEmployees, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, isOpen]);

  // Load selected employee display name when value changes
  useEffect(() => {
    if (value && !selectedEmployee) {
      const loadSelectedEmployee = async () => {
        try {
          const result = await EmployeeAPI.list({
            code: value,
            limit: 1,
          });
          if (result.status === 200 && result.data && result.data.length > 0) {
            setSelectedEmployee(result.data[0]);
          }
        } catch (error) {
          console.error("Error loading selected employee:", error);
        }
      };
      loadSelectedEmployee();
    }
  }, [value, selectedEmployee]);

  const handleSelect = (employee) => {
    setSelectedEmployee(employee);
    onChange(employee.code);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedEmployee(null);
    onChange("");
    setSearchTerm("");
  };

  const formatEmployeeName = (employee) => {
    const name = [employee.first_name, employee.middle_name, employee.last_name]
      .filter(Boolean)
      .join(" ");
    return `${employee.code} - ${name}`;
  };

  return (
    <div
      className={`searchable-employee-select ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="searchable-employee-select__label">
          {label}
          {required && <span className="danger">&nbsp;*</span>}
        </label>
      )}

      <div
        className={`searchable-employee-select__control ${
          isOpen ? "searchable-employee-select__control--open" : ""
        } ${disabled ? "searchable-employee-select__control--disabled" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="searchable-employee-select__value">
          {selectedEmployee ? (
            <>
              <span>{formatEmployeeName(selectedEmployee)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="searchable-employee-select__clear"
                  onClick={handleClear}
                  aria-label="Clear selection"
                >
                  <X size={16} />
                </button>
              )}
            </>
          ) : (
            <span className="searchable-employee-select__placeholder">
              {placeholder}
            </span>
          )}
        </div>
        <Search size={16} className="searchable-employee-select__icon" />
      </div>

      {isOpen && (
        <div className="searchable-employee-select__dropdown">
          <div className="searchable-employee-select__search">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..."
              autoFocus
              className="searchable-employee-select__search-input"
            />
          </div>

          <div className="searchable-employee-select__options">
            {loading ? (
              <div className="searchable-employee-select__loading">
                Loading...
              </div>
            ) : employees.length === 0 ? (
              <div className="searchable-employee-select__empty">
                No employees found
              </div>
            ) : (
              employees.map((employee) => (
                <div
                  key={employee.code}
                  className="searchable-employee-select__option"
                  onClick={() => handleSelect(employee)}
                >
                  <div className="searchable-employee-select__option-code">
                    {employee.code}
                  </div>
                  <div className="searchable-employee-select__option-name">
                    {[
                      employee.first_name,
                      employee.middle_name,
                      employee.last_name,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
